import { InfoOutlined } from '@mui/icons-material';
import { Box, Card, Tooltip, Typography, useTheme } from '@mui/material';
import { JSX } from 'react';
import {
    kpiAccentBarStyles,
    kpiCaptionStyles,
    kpiCardStyles,
    kpiHelpIconStyles,
    kpiLabelBoxStyles,
    kpiLabelStyles,
    kpiValueStyles,
} from './kpiTile.styles';

interface KpiTileProps {
    label: string;
    /** Already formatted and already translated -- this component never touches i18n. */
    value: string;
    /** A second line under the value, for naming which reading of a figure this is. */
    caption?: string;
    /** Turns the label into a tooltip target. Omit unless the figure genuinely needs explaining. */
    help?: string;
    /** At most one tile in a row: if everything is emphasised, nothing is. */
    accented?: boolean;
    testId?: string;
}

/**
 * One headline figure, as every dashboard in the app draws it.
 *
 * Shared between the clinic's page and a therapist's rather than copied, because the two
 * are read as the same kind of statement -- the same tile height, the same tabular digits,
 * the same one accent. A copy would drift, and a figure that looks different on one page
 * than another invites the reader to believe it means something different.
 *
 * Deliberately knows nothing about what it is showing: no currency, no translation, no
 * income mode. Callers hand it finished strings.
 */
export const KpiTile = ({
    label,
    value,
    caption,
    help,
    accented,
    testId,
}: KpiTileProps): JSX.Element => {
    const theme = useTheme();

    return (
        <Card sx={kpiCardStyles} data-testid={testId}>
            {accented && <Box sx={kpiAccentBarStyles} />}

            <Box sx={kpiLabelBoxStyles}>
                <Typography variant="overline" sx={kpiLabelStyles}>
                    {label}
                </Typography>

                {help && (
                    <Tooltip title={help} enterTouchDelay={0}>
                        <InfoOutlined sx={kpiHelpIconStyles(theme)} aria-label={help} />
                    </Tooltip>
                )}
            </Box>

            <Typography variant="h4" sx={kpiValueStyles}>
                {value}
            </Typography>

            {caption && (
                <Typography variant="body2" sx={kpiCaptionStyles}>
                    {caption}
                </Typography>
            )}
        </Card>
    );
};
